import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { MarketplaceItem, MarketplaceItemType } from "@itaas/pep-contracts";
import { getMarketplaceItems } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import {
  formatPricingModel,
  labelMarketplaceStatus,
  labelMarketplaceType,
  marketplaceDescription,
  marketplaceDisplayName,
  marketplaceStatusBadge,
} from "../utils/marketplace-labels";

const TYPES: Array<{ id: MarketplaceItemType | "all"; label: string }> = [
  { id: "all", label: "Tous" },
  { id: "module", label: "Modules" },
  { id: "extension", label: "Extensions" },
  { id: "connector", label: "Connecteurs" },
  { id: "template", label: "Templates" },
];

export function MarketplacePage() {
  const [params, setParams] = useSearchParams();
  const typeFilter = (params.get("type") as MarketplaceItemType | null) ?? "all";
  const productFilter = params.get("product") ?? "";
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMarketplaceItems()
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (typeFilter !== "all") list = list.filter((i) => i.type === typeFilter);
    if (productFilter) list = list.filter((i) => i.productSlugs.includes(productFilter));
    return list;
  }, [items, typeFilter, productFilter]);

  const counts = useMemo(() => {
    const byType = TYPES.slice(1).map((t) => ({
      ...t,
      count: items.filter((i) => i.type === t.id).length,
    }));
    return { all: items.length, byType };
  }, [items]);

  function setType(type: MarketplaceItemType | "all") {
    if (type === "all") setParams({});
    else setParams({ type });
  }

  return (
    <SiteLayout>
      <SeoHead
        title="Marketplace ITaaS"
        description="Modules, extensions, connecteurs et templates pour étendre votre suite ITaaS — mobile money RDC, BI, templates ONG et mines."
        keywords={["marketplace ITaaS", "connecteur Orange Money RDC", "extension ERP Afrique"]}
        canonicalPath="/marketplace"
      />
      <div className="pep-page-hero py-5 mb-0">
        <div className="container">
          <h1 className="display-5 fw-bold mb-2">Marketplace</h1>
          <p className="lead mb-0 opacity-90 col-lg-8">
            Modules, extensions, connecteurs et templates pour personnaliser et intégrer ITaaS à
            votre écosystème.
          </p>
        </div>
      </div>
      <div className="container py-5">
        <div className="d-flex flex-wrap gap-2 mb-4">
          {TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`btn btn-sm ${typeFilter === t.id ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setType(t.id)}
            >
              {t.label}
              {t.id !== "all" && (
                <span className="ms-1 opacity-75">
                  ({counts.byType.find((c) => c.id === t.id)?.count ?? 0})
                </span>
              )}
            </button>
          ))}
        </div>

        {loading && <p className="text-secondary">Chargement…</p>}

        {!loading && filtered.length === 0 && (
          <p className="text-secondary">Aucun élément dans cette catégorie.</p>
        )}

        <div className="row g-4">
          {filtered.map((item) => (
            <div className="col-md-6 col-lg-4" key={item.slug}>
              <article className="card h-100 border-0 shadow-sm pep-marketplace-card">
                <div className="card-body p-4 d-flex flex-column">
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <span className="badge text-bg-light border">
                      {labelMarketplaceType(item.type)}
                    </span>
                    <span className={`badge ${marketplaceStatusBadge(item.status)}`}>
                      {labelMarketplaceStatus(item.status)}
                    </span>
                    {item.publisher === "partner" && (
                      <span className="badge text-bg-info">Partenaire</span>
                    )}
                  </div>
                  <h2 className="h5 fw-bold">
                    <Link className="text-decoration-none" to={`/marketplace/${item.slug}`}>
                      {marketplaceDisplayName(item)}
                    </Link>
                  </h2>
                  <p className="text-secondary small flex-grow-1">
                    {item.taglineFr ?? marketplaceDescription(item)}
                  </p>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="small fw-semibold text-primary">
                      {formatPricingModel(item.pricingModel, item.monthlyUsd)}
                    </span>
                    <Link className="btn btn-sm btn-outline-primary" to={`/marketplace/${item.slug}`}>
                      Détails →
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>
    </SiteLayout>
  );
}
